// src/pages/Register.tsx
//
// Single-request registration: all data + files sent in one POST.
// No API calls until the user clicks "Finish" on the final step.

import React, { useEffect, useState, useRef } from 'react';
import { AuthSkyline } from '../components/common/AuthSkyline';
import { Helmet } from 'react-helmet-async';
import { Area, Button } from '../styles/_app.style';
import {
  Form,
  Name,
  Tagline,
  IconButton,
  BackButton,
  FormActions,
  StepContent,
  RegisterArea,
  StepperArea,
  FormArea,
} from '../styles/login.style';
import { Link, useNavigate } from 'react-router-dom';
import useAuth, { registrationData, ProfilePatch } from '../hooks/auth.service';
import { titles } from '../constants/titles';
import { useToast } from '../contextManager/toast.context';
import { ChevronLeft } from 'lucide-react';

import PersonalDetailsStep from './steps/PersonalDetailsStep';
import { PhotoStep, PhotoStepHandle } from './steps/PhotoStep';
import ExpertiseStep from './steps/ExpertiseStep';
import CollegeDetailsStep from './steps/CollegeDetailsStep';
import { AvailabilityStep } from './steps/AvailabilityStep';
import CVStep from './steps/CVStep';
import QualificationStep from './steps/QualificationStep';
import { ProgressStepper } from '../components/register/ProgressStepper';
import { NAVIGATION_HEIGHT } from '../constants/global';
import RateStep from './steps/RateStep';
import { useScale } from './HeightScaler';
import { BookLogo } from '../components/common/BookLogo';
import { useRegistrationCache } from '../hooks/useRegistrationCache';
import { AvailabilityTimezone } from '../components/register/InfoBlock';

export type Step = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

const stepTaglines = [
  "Let's get started - tell us a bit about yourself",
  'Upload a profile photo',
  'Tell us a bit about your teaching expertise',
  'Set your hourly rate',
  'University / college verification',
  'Input qualifications and standardised test scores',
  'Upload your CV (optional)',
  'When are you usually free to tutor?',
] as const;

const stepLabels = [
  'Details',
  'Photo',
  'Expertise',
  'Rate',
  'College',
  'Qualifications',
  'CV',
  'Availability',
];

const progressWidths = [
  '12.5%', '25%', '37.5%', '50%',
  '62.5%', '75%', '87.5%', '100%',
];

const Register = () => {
  const { registerWithProfile } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { scale } = useScale();

  // ── Wizard state ──────────────────────────────────────────────────
  const [step, setStep] = useState<Step>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [account, setAccount] = useState<registrationData>({
    name: '',
    email: '',
    password: '',
    role: 'tutor',
  });

  const [profile, setProfile] = useState<ProfilePatch>({
    university: 'Oxford',
    availability: {
      monday: [], tuesday: [], wednesday: [], thursday: [],
      friday: [], saturday: [], sunday: [],
    },
  });

  // ── LocalStorage cache ────────────────────────────────────────────
  const { clearCache } = useRegistrationCache(
    step, account, profile,
    setStep, setAccount, setProfile,
  );

  // ── Validation per step ───────────────────────────────────────────
  const [detailsValid, setDetailsValid] = useState(false);
  const [expertiseValid, setExpertiseValid] = useState(false);
  const [collegeValid, setCollegeValid] = useState(false);
  const [rateValid, setRateValid] = useState(false);
  const [availabilityValid, setAvailabilityValid] = useState(false);
  const [photoValid, setPhotoValid] = useState(true);
  const [CVValid, setCVValid] = useState(false);
  const [qualificationValid, setQualificationValid] = useState(false);

  // ── Photo ref (to get cropped blob without uploading) ─────────────
  const photoRef = useRef<PhotoStepHandle>(null);

  // ── Progress bar side effect ──────────────────────────────────────
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--progress-width',
      progressWidths[step],
    );
    document.body.classList.add('register-page-active');
    return () => {
      document.documentElement.style.removeProperty('--progress-width');
      document.body.classList.remove('register-page-active');
    };
  }, [step]);

  // ── Navigation ────────────────────────────────────────────────────
  const next = () => setStep((s) => (s < 7 ? ((s + 1) as Step) : s));
  const prev = () => setStep((s) => (s > 0 ? ((s - 1) as Step) : s));

  const savePatch = (p: ProfilePatch) => {
    setProfile((prev) => ({ ...prev, ...p }));
  };

  // ── Can we move forward? ──────────────────────────────────────────
  const canProceed = () => {
    if (step === 0) return detailsValid;
    return true;
  };

  // ── handleNext ────────────────────────────────────────────────────
  const handleNext = async () => {
    // Steps 0-6: just advance locally, no API calls
    if (step < 7) {
      if (step === 1) {
        await photoRef.current?.saveIfNeeded();
      }
      next();
      return;
    }

    // ── Step 7 (Finish): ONE request does everything ──────────────
    setIsSubmitting(true);

    try {
      await registerWithProfile(account, profile);

      clearCache();
      addToast({
        variant: 'info',
        title: 'Account created! Check your inbox for a verification link.',
      });
      navigate('/strippedTimeline');
    } catch (err) {
      console.error('Registration failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>{titles.register}</title>
      </Helmet>
      <RegisterArea>
        <AuthSkyline />
        <Area
          css={{
            height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`,
            marginTop: `calc(${NAVIGATION_HEIGHT})`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <StepperArea>
            <ProgressStepper steps={stepLabels} currentStep={step} />
          </StepperArea>

          <FormArea>
            <Form
              // size={step === 7 ? 'lg' : 'sm'}
              onSubmit={(e) => e.preventDefault()}
            >
              {/* ── Header: always show logo, back button when step > 0 ── */}
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                  minHeight: '48px',
                }}
              >
                {step > 0 && (
                  <div style={{ position: 'absolute', left: 0 }}>
                    <BackButton aria-label="Go back" onClick={prev}>
                      <IconButton type="button">
                        <ChevronLeft size={25} />
                      </IconButton>
                    </BackButton>
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: 'max-content',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: '1.8rem',
                      lineHeight: '1',
                      color: 'var(--colors-slate12, #1e293b)',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    tute
                  </span>
                  <div
                    style={{
                      width: '100%',
                      height: '16px',
                      marginTop: '0.1rem',
                    }}
                  >
                    <BookLogo />
                  </div>
                </div>
              </div>

              <Tagline style={{ marginBottom: '0.5rem' }}>
                {stepTaglines[step]}
              </Tagline>

              {/* ── Step content ── */}
              <StepContent>
                {step === 0 && (
                  <PersonalDetailsStep
                    data={account}
                    onChange={setAccount}
                    onValidityChange={setDetailsValid}
                  />
                )}

                {step === 1 && (
                  <PhotoStep
                    ref={photoRef}
                    data={profile}
                    onChange={savePatch}
                    onValidityChange={setPhotoValid}
                  />
                )}

                {step === 2 && (
                  <ExpertiseStep
                    data={profile}
                    onChange={savePatch}
                    onValidityChange={setExpertiseValid}
                  />
                )}

                {step === 3 && (
                  <RateStep
                    data={profile}
                    onChange={savePatch}
                    onValidityChange={setRateValid}
                  />
                )}

                {step === 4 && (
                  <CollegeDetailsStep
                    data={profile}
                    onChange={savePatch}
                    onValidityChange={setCollegeValid}
                  />
                )}

                {step === 5 && (
                  <QualificationStep
                    data={profile}
                    onChange={savePatch}
                    onValidityChange={setQualificationValid}
                  />
                )}

                {step === 6 && (
                  <CVStep
                    data={profile}
                    onChange={savePatch}
                    onValidityChange={setCVValid}
                  />
                )}

                {step === 7 && (
                  <>
                  <AvailabilityTimezone
                    timezone={
                      profile.timezone ||
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    }
                    onTimezoneChange={(tz) => savePatch({ timezone: tz })}
                  />
                  <AvailabilityStep
                    data={profile}
                    onChange={savePatch}
                    onValidityChange={setAvailabilityValid}
                  />
                  </>
                )}
              </StepContent>

              {/* ── Footer ── */}
              <FormActions>
                <Button
                  type="button"
                  block
                  tone="success"
                  disabled={!canProceed() || isSubmitting}
                  onClick={handleNext}
                >
                  {isSubmitting
                    ? 'Creating account…'
                    : step < 7
                      ? 'Next'
                      : 'Finish'}
                </Button>

                <Link
                  to="/login"
                  style={{ marginTop: '1rem', display: 'inline-block' }}
                >
                  Already have an account? Sign in here
                </Link>
              </FormActions>
            </Form>
          </FormArea>
        </Area>
      </RegisterArea>
    </>
  );
};

export default Register;